import { useState } from 'react'
import styled from 'styled-components'

import {
  Button,
  Flex,
  Heading,
  StatCard,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableProvider,
  TableRow,
  Text,
  theme,
} from '@percent/lemonade'
import { formatAmount } from '@percent/utility'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'
import { OrganisationDonations } from '../../types/organisationProfile.types'
import { ProfileLink } from '../ProfileLink/ProfileLink'
import { SectionTitle } from '../SectionTitle/SectionTitle'

const Tiles = styled.div`
  ${({ theme }) => `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(calc(${theme.spacing.xxl} * 4), 1fr));
    gap: ${theme.spacing.m};
  `}
`

// The API orders currencies most-used first: the top ones are tiles, the rest a compact
// list behind "Show more".
const MAX_CURRENCY_TILES = 3

// Decimal places from Intl, so hosts needn't pass currency info.
const toMajorUnits = (amount: number, currency: string) =>
  amount /
  10 **
    (new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits ?? 2)

export type OrganisationDonationSummaryProps = {
  organisationId: string
  donations: OrganisationDonations
}

export function OrganisationDonationSummary({
  organisationId,
  donations,
}: Readonly<OrganisationDonationSummaryProps>) {
  const { t, locale } = useTranslation()
  const { links } = useOrganisationProfileContext()
  const [showMoreCurrencies, setShowMoreCurrencies] = useState(false)
  const hasDonations = donations.totals.length > 0
  const tileTotals = donations.totals.slice(0, MAX_CURRENCY_TILES)
  const moreTotals = donations.totals.slice(MAX_CURRENCY_TILES)

  const formatTotal = (amount: number, currency: string) =>
    formatAmount({
      currencyCode: currency,
      value: toMajorUnits(amount, currency),
      language: locale,
    })

  const currencyNames = new Intl.DisplayNames([locale], { type: 'currency' })

  const currencyColumns: TableColumn[] = [
    {
      id: 'currency',
      content: t('profile.donations.currency'),
      props: { width: '50%' },
    },
    {
      id: 'total',
      content: t('profile.donations.total'),
      props: { width: '30%' },
    },
    {
      id: 'count',
      content: t('profile.donations.count'),
      props: { width: '20%' },
    },
  ]

  const formatDate = (date: string | null) =>
    date
      ? new Date(date).toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—'

  return (
    <Flex
      direction="column"
      gap={16}
      data-testid="organisation-donation-summary"
    >
      <Flex direction="column" gap={4}>
        <Flex justify="space-between" align="center" gap={16}>
          <Heading level="h2">
            <SectionTitle>{t('profile.donations.title')}</SectionTitle>
          </Heading>
          {hasDonations && links.donations && (
            <ProfileLink href={links.donations(organisationId)}>
              {t('profile.donations.viewAll')}
            </ProfileLink>
          )}
        </Flex>
        {hasDonations && (
          <Text size="small" color={theme.semantic.contentTertiary}>
            {t('profile.donations.description')}
          </Text>
        )}
      </Flex>
      {!hasDonations ? (
        <Flex direction="column" gap={4}>
          <Text>{t('profile.donations.empty')}</Text>
          <Text size="small" color={theme.semantic.contentTertiary}>
            {t('profile.donations.emptyDescription')}
          </Text>
        </Flex>
      ) : (
        <Tiles>
          {tileTotals.map(({ amount, currency, count }) => (
            <StatCard
              key={currency}
              label={t('profile.donations.total')}
              value={formatTotal(amount, currency)}
              subtitle={t('profile.donations.subtitle', { currency, count })}
            />
          ))}
          <StatCard
            label={t('profile.donations.firstDonation')}
            value={formatDate(donations.firstDonationAt)}
          />
          <StatCard
            label={t('profile.donations.lastDonation')}
            value={formatDate(donations.lastDonationAt)}
          />
        </Tiles>
      )}
      {moreTotals.length > 0 && (
        <>
          {showMoreCurrencies && (
            <TableProvider
              columns={currencyColumns}
              totalResults={moreTotals.length}
              isLoading={false}
              illustration="no-results"
              emptyTableTitle={t('profile.donations.empty')}
            >
              <Table>
                <TableBody>
                  {moreTotals.map(({ amount, currency, count }) => (
                    <TableRow key={currency}>
                      <TableCell>
                        <Flex align="baseline" gap={8}>
                          <Text>{currencyNames.of(currency)}</Text>
                          <Text
                            size="small"
                            color={theme.semantic.contentTertiary}
                          >
                            {currency}
                          </Text>
                        </Flex>
                      </TableCell>
                      <TableCell>{formatTotal(amount, currency)}</TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableProvider>
          )}
          <Flex>
            <Button
              variant="tertiary"
              size="small"
              onClick={() => setShowMoreCurrencies((shown) => !shown)}
            >
              {showMoreCurrencies
                ? t('profile.donations.showFewerCurrencies')
                : t('profile.donations.showMoreCurrencies', {
                    count: moreTotals.length,
                  })}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  )
}
